import { ShieldCheck, ShieldAlert, Clock } from 'lucide-react'
import type { LicenseInfo } from '../api'

function plural(n: number): string {
  const a = Math.abs(n) % 100, b = a % 10
  if (a > 10 && a < 20) return 'дней'
  if (b > 1 && b < 5) return 'дня'
  if (b === 1) return 'день'
  return 'дней'
}

/**
 * Бейдж остатка лицензии в шапке: цвет зависит от запаса дней,
 * клик ведёт на продление. Скрывается для бессрочных лицензий
 * (expires_at = 0) и пока данные не загружены.
 *
 * Цвета: >14 дн — зелёный, 4-14 — жёлтый, 1-3 — оранжевый (пульс),
 * истекла/приостановлена — красный (пульс, ярко).
 */
export function LicenseBadge({ lic, onRenew }: { lic: LicenseInfo | null; onRenew: () => void }) {
  if (!lic || !lic.expires_at) return null  // нет данных или бессрочная

  const days = lic.days_left ?? Math.ceil(((lic.expires_at || 0) * 1000 - Date.now()) / 86_400_000)
  const expired = lic.is_valid === false || lic.suspended || lic.revoked || days <= 0

  let tone: string
  let pulse = false
  let Icon = ShieldCheck
  let text: string
  let full: string

  if (expired) {
    tone = 'border-red-500/60 text-red-300 bg-red-500/20'
    pulse = true
    Icon = ShieldAlert
    text = 'Истекла'
    full = lic.suspended ? 'Лицензия приостановлена' : 'Лицензия истекла'
  } else if (days <= 3) {
    tone = 'border-orange-500/60 text-orange-300 bg-orange-500/20'
    pulse = true
    Icon = ShieldAlert
    text = `${days} ${plural(days)}`
    full = `Лицензия истекает через ${days} ${plural(days)}`
  } else if (days <= 14) {
    tone = 'border-amber-500/50 text-amber-300 bg-amber-500/15'
    Icon = Clock
    text = `${days} ${plural(days)}`
    full = `Осталось ${days} ${plural(days)} лицензии`
  } else {
    tone = 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10'
    Icon = ShieldCheck
    text = `${days} ${plural(days)}`
    full = `Лицензия активна: ${days} ${plural(days)}`
  }

  return (
    <button
      onClick={onRenew}
      title={`${full}. Нажмите, чтобы продлить.`}
      className={`inline-flex items-center gap-1.5 rounded-md border h-7 pl-2 pr-1.5 text-[11px] font-medium transition-colors shrink-0 hover:brightness-125 ${tone} ${pulse ? 'animate-pulse' : ''}`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      <span className="tabular-nums whitespace-nowrap">{text}</span>
      <span className="hidden md:inline rounded bg-current/15 px-1.5 py-0.5 text-[10px] leading-none ml-0.5 opacity-90">
        Продлить
      </span>
    </button>
  )
}
